import { setup, assign } from 'xstate';
import { getStore, storeKeys } from 'core/storage/store';

// How many recent queries we keep (mirrors the mobile search palette).
const RECENT_LIMIT = 8;

// Shared search state, held in one place so the header search field and the
// search screen reflect the same query / scope filters / recent searches
// without passing params around. Data fetching stays in the react-query
// `useSearch` hook, keyed off this context.
export interface SearchMachineContext {
  // The active/committed search term (what's being searched and shown). Each
  // input keeps its own live text buffer and commits here on submit.
  query: string;
  filterBoxId: string | null;
  filterCollectionId: string | null;
  recentSearches: string[];
}

export enum EventType {
  SetQuery = 'SET_QUERY',
  Submit = 'SUBMIT',
  SetBoxFilter = 'SET_BOX_FILTER',
  SetCollectionFilter = 'SET_COLLECTION_FILTER',
  Clear = 'CLEAR',
  DeleteRecent = 'DELETE_RECENT',
  LoadRecents = 'LOAD_RECENTS',
}

export type SearchMachineEvent =
  | { type: EventType.SetQuery; query: string }
  | { type: EventType.Submit; query: string }
  | { type: EventType.SetBoxFilter; boxId: string | null }
  | { type: EventType.SetCollectionFilter; collectionId: string | null }
  | { type: EventType.Clear }
  | { type: EventType.DeleteRecent; query: string }
  | { type: EventType.LoadRecents };

const readRecents = (): string[] =>
  getStore().getItem(storeKeys.recentSearches) ?? [];

const persistRecents = (recents: string[]): string[] => {
  getStore().setItem(storeKeys.recentSearches, recents);
  return recents;
};

// Promote a query to the front (case-insensitive dedup), cap the list, persist.
const recordRecent = (recents: string[], query: string): string[] => {
  const trimmed = query.trim();
  if (!trimmed) return recents;
  const deduped = recents.filter(
    (q) => q.toLowerCase() !== trimmed.toLowerCase(),
  );
  return persistRecents([trimmed, ...deduped].slice(0, RECENT_LIMIT));
};

export const searchMachine = setup({
  types: {
    context: {} as SearchMachineContext,
    events: {} as SearchMachineEvent,
  },
}).createMachine({
  id: 'search',
  context: {
    query: '',
    filterBoxId: null,
    filterCollectionId: null,
    recentSearches: [],
  },
  initial: 'ready',
  states: { ready: {} },
  on: {
    [EventType.SetQuery]: {
      actions: assign(({ event }) => ({ query: event.query })),
    },
    [EventType.Submit]: {
      actions: assign(({ context, event }) => ({
        query: event.query,
        recentSearches: recordRecent(context.recentSearches, event.query),
      })),
    },
    [EventType.SetBoxFilter]: {
      // Changing the box clears the collection (it belonged to the old box).
      actions: assign(({ event }) => ({
        filterBoxId: event.boxId,
        filterCollectionId: null,
      })),
    },
    [EventType.SetCollectionFilter]: {
      actions: assign(({ event }) => ({
        filterCollectionId: event.collectionId,
      })),
    },
    [EventType.Clear]: {
      actions: assign({ query: '' }),
    },
    [EventType.DeleteRecent]: {
      actions: assign(({ context, event }) => ({
        recentSearches: persistRecents(
          context.recentSearches.filter((q) => q !== event.query),
        ),
      })),
    },
    [EventType.LoadRecents]: {
      actions: assign({ recentSearches: () => readRecents() }),
    },
  },
});
